import { gql } from "@apollo/client"
import styled from "@emotion/styled"
import { LinkText, mq, Text, theme } from "@pallet-hq/hegel"
import UpsellInfoModal from "components/Modals/UpsellInfoModal"
import { useState } from "react"
import { Fade } from "react-reveal"
import { WelcomeDetailsSection_applylist } from "__generated__/WelcomeDetailsSection_applylist"
import ShortCandidatePreview from "./CandidatePreview/Short"
import TaggedBlock from "./TaggedBlock"

const DetailsSectionFragment = gql`
    fragment WelcomeDetailsSection_applylist on ApplylistType {
        id
        group {
            id
            name
            upsellDetails
            topExperienceLevels
            topIndustries
            topJobTypes
            topLocations
            shortFeaturedCandidates: featuredPublicCandidates(
                numCandidates: 4
                includeUndropped: true
            ) {
                id
                ...WelcomeShortCandidatePreview_candidate
            }
        }
        ...UpsellInfoModal_applylist
    }
    ${ShortCandidatePreview.fragment}
    ${UpsellInfoModal.fragment}
`

type DetailsSectionProps = {
    applylist: WelcomeDetailsSection_applylist
}

const DetailsSection = ({ applylist }: DetailsSectionProps) => {
    const [isModalVisible, setModalVisibility] = useState(false)
    const group = applylist.group!

    return (
        <Fade>
            <Wrapper hasDetails={!!group.upsellDetails}>
                <LeftBlock>
                    <TextWrapper hasDetails={!!group.upsellDetails}>
                        <Text
                            variant={["headlineLarge", "titleMedium"]}
                            mb="small"
                        >
                            {group.name}
                        </Text>
                        <Text
                            variant={["paragraphMedium", "paragraphLarge"]}
                            mb="small"
                        >
                            {group.upsellDetails}
                        </Text>
                        {group.upsellCurationStrategy && (
                            <LinkText
                                ml="-8px"
                                onClick={() => setModalVisibility(true)}
                            >
                                How are candidates curated?
                            </LinkText>
                        )}
                    </TextWrapper>
                </LeftBlock>
                <RightBlock hasDetails={!!group.upsellDetails}>
                    <RightBlockInnerDiv hasDetails={!!group.upsellDetails}>
                        <MasonryRow>
                            <TaggedBlock
                                label="Relevant markets"
                                tags={group.topIndustries}
                            />
                            {group.shortFeaturedCandidates[0] && (
                                <ShortCandidatePreview
                                    candidate={group.shortFeaturedCandidates[0]}
                                />
                            )}
                        </MasonryRow>
                        <MasonryRow>
                            {group.shortFeaturedCandidates[1] && (
                                <ShortCandidatePreview
                                    candidate={group.shortFeaturedCandidates[1]}
                                />
                            )}
                            <TaggedBlock
                                label="Popular roles"
                                tags={group.topJobTypes}
                            />
                        </MasonryRow>
                        <MasonryRow>
                            <TaggedBlock
                                label="Popular locations"
                                tags={group.topLocations}
                            />
                            {group.shortFeaturedCandidates[2] && (
                                <ShortCandidatePreview
                                    candidate={group.shortFeaturedCandidates[2]}
                                />
                            )}
                        </MasonryRow>
                        <MasonryRow>
                            {group.shortFeaturedCandidates[3] && (
                                <ShortCandidatePreview
                                    candidate={group.shortFeaturedCandidates[3]}
                                />
                            )}
                            <TaggedBlock
                                label="Popular experience levels"
                                tags={group.topExperienceLevels}
                            />
                        </MasonryRow>
                    </RightBlockInnerDiv>
                </RightBlock>
            </Wrapper>
            {group.upsellCurationStrategy && (
                <UpsellInfoModal
                    isOpen={isModalVisible}
                    hideModal={() => setModalVisibility(false)}
                    applylist={applylist}
                />
            )}
        </Fade>
    )
}

DetailsSection.fragment = DetailsSectionFragment
export default DetailsSection

const Wrapper = styled.section<{ hasDetails: boolean }>`
    width: 100%;
    margin: ${theme.space.xxlarge} auto ${theme.space.xlarge};
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 1500px;

    ${({ hasDetails }) =>
        mq({
            flexDirection: ["column", hasDetails ? "row" : "column"],
            gap: [theme.space.large, 0],
        })};
`

const TextWrapper = styled.div<{ hasDetails: boolean }>`
    max-width: 580px;
    box-sizing: border-box;

    ${({ hasDetails }) =>
        mq({
            padding: [
                `0 ${theme.space.small}`,
                hasDetails
                    ? `0 ${theme.space.small} 0 ${theme.space.large}`
                    : `0 ${theme.space.small}`,
            ],
            marginLeft: ["0", "auto"],
            textAlign: ["left", hasDetails ? "left" : "center"],
            margin: ["0 auto", hasDetails ? "0 0 0 auto" : "0 auto"],
            maxWidth: [380, 580],
        })}
`

const LeftBlock = styled.div`
    width: 100%;
`

const RightBlock = styled.div<{ hasDetails: boolean }>`
    width: 100%;
    box-sizing: border-box;
    padding-right: ${theme.space.xxsmall};

    ${({ hasDetails }) =>
        mq({
            display: ["block", hasDetails ? "flex" : "block"],
            padding: [
                `0 ${theme.space.xxsmall}`,
                hasDetails
                    ? `0 ${theme.space.xxsmall} 0 0`
                    : `0 ${theme.space.xxsmall}`,
            ],
        })};
`

const RightBlockInnerDiv = styled.div<{ hasDetails: boolean }>`
    display: flex;
    width: 100%;
    flex-direction: column;
    ${({ hasDetails }) =>
        mq({ alignItems: ["center", hasDetails ? "flex-end" : "center"] })};
`

const MasonryRow = styled.div`
    display: flex;
    gap: 24px;
    margin-bottom: ${theme.space.medium};

    ${mq({ gap: [12, 24] })};
`
